/* MUI */
import { Box, Paper, TableCell, Typography } from '@mui/material';

/*  Shared  */
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import { ManagedTable } from 'application/Shared/Components/Table';
import { Route } from 'application/Shared/Helpers';

/* Hooks */
import { useFetchUsersAsync } from 'hooks/dataAccess/User/user';

/*  Objects  */
import { Application, ApplicationBusinessOwner } from 'objects/Application';
import {
    Contract,
    ContractAlertingIconWithText,
    ContractDate,
    ContractTCV,
    ContractType
} from 'objects/Contract';

/* React */
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { csvFields, handleRows } from './functions';

const headers = (t) => [
    {
        align: 'left',
        field: 'name',
        renderHeader: <strong>{t('Common.application')}</strong>,
        sortable: true,
        sortableFieldType: 'string',
        sortableKey: 'application.name',
        tooltip: 'application',
        width: '20%'
    },
    {
        align: 'left',
        field: 'business_owner',
        renderHeader: <strong>{t('Common.business_owner')}</strong>,
        tooltip: 'business_owner',
        width: '20%'
    },
    {
        align: 'left',
        field: 'alerting',
        renderHeader: <strong>{t('Common.alerting')}</strong>,
        width: '10%'
    },
    {
        align: 'left',
        field: 'type',
        renderHeader: <strong>{t('Common.type')}</strong>,
        tooltip: 'contract_management_monthly_renewal_date',
        width: '20%'
    },
    {
        align: 'left',
        field: 'renewalDate',
        renderHeader: <strong>{t('Common.contract_date')}</strong>,
        tooltip: 'renewalDate',
        width: '15%'
    },
    {
        align: 'left',
        field: 'tcv',
        renderHeader: <strong>{t('Common.tcv')}</strong>,
        tooltip: 'contract_management_tcv',
        width: '15%'
    }
];

const parameters = {
    includeApplication: true,
    includeUsers: true,
    type: '*'
};

const searchFields = [
    'application.name',
    'application.category',
    'application.subCategory',
    'type'
];

export const ContractList = () => {
    const { t } = useTranslation();
    const { clientId, partnerId } = useParams();
    const [rowMap, setRowMap] = useState({});

    const route = Route.buildBaseAdminRoute({ clientId, partnerId });

    const { getUsers } = useFetchUsersAsync();

    const handleRowsFn = useCallback(
        (rows) => {
            handleRows(rows, clientId, getUsers, setRowMap, t);
        },
        [clientId, getUsers, t]
    );

    const csvData = useMemo(() => Object.values(rowMap), [rowMap]);

    const tableHeaders = useMemo(() => headers(t), [t]);

    return (
        <Paper
            elevation={0}
            variant="outlined"
            data-trackid="adminContractPagesList"
            data-testid="adminContractPagesList"
        >
            <Box>
                <Typography variant="h4">
                    {t('ContractManagement.list')}
                </Typography>
                <ManagedTable
                    csvData={csvData}
                    csvExport
                    csvExportFields={csvFields}
                    csvExportFileName="contracts"
                    handleRows={handleRowsFn}
                    headers={tableHeaders}
                    parameters={parameters}
                    renderBody={(row) => (
                        <Application
                            data={row.application}
                            nameLinkTo={`${route}/application/${row.application.id}/contract`}
                        >
                            <TableCell>
                                <ApplicationNameLinkCell withCategory />
                            </TableCell>
                            <TableCell>
                                <ApplicationBusinessOwner />
                            </TableCell>
                            <Contract data={row}>
                                <TableCell>
                                    <ContractAlertingIconWithText />
                                </TableCell>
                                <TableCell>
                                    <ContractType />
                                </TableCell>
                                <TableCell>
                                    <ContractDate />
                                </TableCell>
                                <TableCell>
                                    <ContractTCV />
                                </TableCell>
                            </Contract>
                        </Application>
                    )}
                    renderBodyKey="id"
                    renderFooter
                    rowsApi="/contract"
                    rowsApiPreload
                    rowsPerPage="dynamic"
                    searchEnabled
                    searchFields={searchFields}
                    sortDirection="ASC"
                    sortFieldType="string"
                    sortKey="application.name"
                />
            </Box>
        </Paper>
    );
};
